import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { mdiCheckAll } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { CampaignApiError, useAnswerEntry, useCampaign, useCampaignUnit, useCompleteUnit } from '../../hooks/useCampaigns'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { AppIcon } from '../ui/icon'
import { Skeleton } from '../ui/skeleton'
import { CampaignEntryField } from './CampaignEntryField'
import { UNIT_STATUS } from './campaign-status'
import { StatusPill } from './StatusPill'

/**
 * The agent's form for one unit (spec 2026-10-20-12 §6): one section per
 * grouping, one block per surveyed asset, each value known at launch shown
 * with a gesture to confirm it. Finishing needs every required question;
 * empty required properties only ask for a confirmation.
 */
export function UnitFormPage() {
    const { org, campaignUid, unitUid } = useParams({ from: '/$org/campaigns/$campaignUid/units/$unitUid' })
    const { t } = useTranslation('campaigns')
    const { data: campaign } = useCampaign(org, campaignUid)
    const { data: unit, isLoading, error } = useCampaignUnit(org, campaignUid, unitUid)
    const answer = useAnswerEntry(org, campaignUid)
    const complete = useCompleteUnit(org, campaignUid, unitUid)
    const [blocking, setBlocking] = useState<string[]>([])
    const [emptyRequired, setEmptyRequired] = useState<string[] | null>(null)
    useBreadcrumbs([
        { title: t('title'), path: `/${org}/campaigns` },
        { title: campaign?.name ?? '', path: `/${org}/campaigns/${campaignUid}` },
        { title: unit?.asset.name ?? '' },
    ])

    const finish = async (force: boolean) => {
        setBlocking([])
        try {
            await complete.mutateAsync(force)
            setEmptyRequired(null)
        } catch (e) {
            const err = e as CampaignApiError
            if (err.code === 'required_questions') setBlocking(err.fields.map((f) => f.message))
            else if (err.code === 'required_properties_empty') setEmptyRequired(err.fields.map((f) => f.message))
            else setBlocking([err.message])
        }
    }

    if (isLoading) return <Skeleton className="h-64 w-full" />
    if (error || !unit) {
        return (
            <Alert variant="destructive" data-testid="campaign-unit-error">
                <AlertDescription>{t('unit_not_found')}</AlertDescription>
            </Alert>
        )
    }
    const place = [unit.site?.name, unit.building && unit.building.uid !== unit.asset.uid ? unit.building.name : undefined]
        .filter(Boolean)
        .join(' · ')

    return (
        <div className="space-y-4 pb-24 md:pb-6" data-testid="campaign-unit-page">
            <PageHeader
                icon={SECTION_ICONS.campaigns}
                title={unit.asset.name}
                description={place || undefined}
                titleAside={<StatusPill map={UNIT_STATUS} family="unit_status" value={unit.status} testId="campaign-unit-status" />}
            />
            {!unit.can_fill && (
                <Alert data-testid="campaign-unit-readonly">
                    <AlertDescription>
                        {unit.campaign_status === 'launched' ? t('unit_readonly') : t('unit_closed')}
                    </AlertDescription>
                </Alert>
            )}
            {unit.groupings.map((g) => (
                <Card key={g.uid} data-testid={`campaign-grouping-${g.uid}`}>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">{g.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {g.targets.map((target) => (
                            <div key={target.asset.uid} data-testid={`campaign-target-${target.asset.uid}`}>
                                {target.asset.uid !== unit.asset.uid && (
                                    <p className="text-sm font-semibold text-muted-foreground">{target.asset.name}</p>
                                )}
                                <div className="divide-y">
                                    {target.entries.map((entry) => (
                                        <CampaignEntryField
                                            key={entry.uid}
                                            entry={entry}
                                            canFill={unit.can_fill}
                                            testId={`campaign-entry-${entry.uid}`}
                                            onSave={async (value, confirmed) => {
                                                await answer.mutateAsync({ entryUid: entry.uid, answer: value, confirmed })
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            ))}
            {blocking.length > 0 && (
                <Alert variant="destructive" data-testid="campaign-unit-blocking">
                    <AlertTitle>{t('complete_blocked')}</AlertTitle>
                    <AlertDescription>
                        <ul className="list-disc pl-5">
                            {blocking.map((b, i) => (
                                <li key={i}>{b}</li>
                            ))}
                        </ul>
                    </AlertDescription>
                </Alert>
            )}
            {unit.can_fill && (
                <div className="fixed inset-x-0 bottom-0 z-10 border-t bg-background p-3 md:static md:border-0 md:bg-transparent md:p-0">
                    <Button
                        className="w-full md:w-auto"
                        onClick={() => void finish(false)}
                        disabled={complete.isPending}
                        data-testid="campaign-unit-complete"
                    >
                        <AppIcon path={mdiCheckAll} size="sm" className="mr-1" />
                        {unit.status === 'done' ? t('complete_again') : t('complete')}
                    </Button>
                </div>
            )}
            <AlertDialog open={emptyRequired !== null} onOpenChange={(open) => !open && setEmptyRequired(null)}>
                <AlertDialogContent data-testid="campaign-unit-force-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('complete_anyway_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('complete_anyway_description')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <ul className="list-disc pl-5 text-sm">
                        {(emptyRequired ?? []).map((label, i) => (
                            <li key={i}>{label}</li>
                        ))}
                    </ul>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="campaign-unit-force-cancel">{t('cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => void finish(true)} data-testid="campaign-unit-force-confirm">
                            {t('complete_anyway')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
